import { useEffect, useMemo, useState } from "react";
import { useDashboardSocket } from "../live/DashboardSocketContext";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  fetchFleetDaily,
  fetchStopsPareto,
  fetchSessionKpis,
  FleetDailyKpi,
  ParetoCategory,
  SessionKpi,
} from "../lib/api";
import { getDeviceScope, scopeToDeviceIdParam } from "../lib/scope";
import { DateRange, fromDayString } from "../lib/dateRange";
import { chartPixelRatio } from "../lib/uiScale";
import { DateRangeFilter } from "../components/DateRangeFilter";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const CREW_RATE_PER_HOUR = 30;

export function OverviewPage({
  range,
  onRangeChange,
}: {
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
}) {
  const { startDate, endDate } = range;
  const [daily, setDaily] = useState<FleetDailyKpi[]>([]);
  const [pareto, setPareto] = useState<ParetoCategory[]>([]);
  const [sessions, setSessions] = useState<SessionKpi[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { sessionRefetch } = useDashboardSocket();
  const [reloadKey, setReloadKey] = useState(0);
  // Dashboard aggregates are computed from finished sessions, so a session
  // ending changes them — refetch on the delta rather than leaving stale
  // totals on screen until the user changes the date range.
  useEffect(() => {
    if (sessionRefetch.seq === 0) return;
    setReloadKey((k) => k + 1);
  }, [sessionRefetch]);

  useEffect(() => {
    let cancelled = false;
    // Only blank the page on the first load — a live refetch swaps numbers in
    // place instead of throwing the whole dashboard back to "Loading…".
    setError(null);
    (async () => {
      try {
        const scope = await getDeviceScope();
        // External customer with no devices in their company pool → show
        // nothing rather than querying the unscoped (whole-fleet) endpoints.
        if (scope !== null && scope.length === 0) {
          if (!cancelled) {
            setDaily([]);
            setPareto([]);
            setSessions([]);
          }
          return;
        }
        const deviceId = scopeToDeviceIdParam(scope);
        const [d, p, s] = await Promise.all([
          fetchFleetDaily({ startDate, endDate, deviceId }),
          fetchStopsPareto({ startDate, endDate, groupBy: "cause", deviceId }),
          fetchSessionKpis({ startDate, endDate, limit: 200, deviceId }),
        ]);
        if (!cancelled) {
          setDaily(d);
          setPareto(p.categories);
          setSessions(s);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, reloadKey]);

  const totals = useMemo(() => {
    const totalSessions = daily.reduce((s, d) => s + d.total_sessions, 0);
    const completed = daily.reduce((s, d) => s + d.completed_sessions, 0);
    const totalAcres = daily.reduce((s, d) => s + d.total_acres, 0);
    const totalMowingSec = daily.reduce(
      (s, d) => s + d.total_mowing_seconds,
      0,
    );
    const totalInterventions = daily.reduce(
      (s, d) => s + (d.interventions || 0),
      0,
    );
    const mowersActive = Math.max(
      ...daily.map((d) => d.robots_active || 0),
      0,
    );
    const laborHours = totalMowingSec / 3600;
    const productivities = daily
      .map((d) => d.avg_productivity_acres_per_hour)
      .filter((p) => p > 0);
    const avgProd =
      productivities.length > 0
        ? productivities.reduce((a, b) => a + b, 0) / productivities.length
        : 0;
    return {
      totalSessions,
      completed,
      completionPct:
        totalSessions > 0 ? Math.round((completed / totalSessions) * 100) : 0,
      totalAcres,
      laborHours,
      laborValue: laborHours * CREW_RATE_PER_HOUR,
      mowersActive,
      totalInterventions,
      acresPerIntervention:
        totalInterventions > 0 ? totalAcres / totalInterventions : null,
      avgProd,
    };
  }, [daily]);

  const outcomes = useMemo(() => {
    let completed = 0;
    let pausedResumed = 0;
    let intervened = 0;
    for (const s of sessions) {
      if (s.interventions > 0) intervened++;
      else if (s.session_outcome === "COMPLETE") completed++;
      else pausedResumed++;
    }
    const total = sessions.length;
    return {
      total,
      completed,
      pausedResumed,
      intervened,
      pct: (n: number) =>
        total > 0 ? Math.round((n / total) * 100) : 0,
    };
  }, [sessions]);

  const chartData = useMemo(() => {
    const sorted = [...daily].sort((a, b) => a.date.localeCompare(b.date));
    return {
      // `date` is a bare YYYY-MM-DD day; parse it as local (not UTC, which
      // would shift every label back a day west of GMT).
      labels: sorted.map((d) =>
        (fromDayString(d.date) ?? new Date(d.date)).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      ),
      datasets: [
        {
          label: "Sessions",
          data: sorted.map((d) => d.total_sessions),
          backgroundColor: "#5E85C4",
          borderRadius: 3,
          yAxisID: "y1",
        },
        {
          label: "Area (ac)",
          data: sorted.map((d) => Math.round(d.total_acres * 100) / 100),
          backgroundColor: "#22c55e",
          borderRadius: 3,
          yAxisID: "y2",
        },
      ],
    };
  }, [daily]);

  const chartOptions = useMemo(() => {
    const grid = "rgba(128,128,128,0.14)";
    const tick = { font: { size: 11, family: "Raleway, sans-serif" }, color: "#7b8794" };
    return {
      responsive: true,
      maintainAspectRatio: false,
      // Counteract the global `body { zoom }` so the canvas isn't upscaled and
      // resampled into a blurry mess. See lib/uiScale.ts.
      devicePixelRatio: chartPixelRatio(),
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: {
          labels: {
            font: { size: 11, family: "Raleway, sans-serif", weight: 700 as const },
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: "circle" as const,
            padding: 14,
            color: "#7b8794",
          },
        },
        tooltip: {
          backgroundColor: "#0F1B2D",
          titleFont: { size: 12, family: "Raleway, sans-serif", weight: 700 as const },
          bodyFont: { size: 12, family: "Raleway, sans-serif" },
          padding: 10,
          cornerRadius: 6,
          displayColors: true,
          usePointStyle: true,
        },
      },
      scales: {
        x: { grid: { display: false }, border: { color: grid }, ticks: tick },
        y1: {
          position: "left" as const,
          beginAtZero: true,
          ticks: { ...tick, precision: 0 },
          grid: { color: grid },
          border: { display: false },
          title: {
            display: true,
            text: "Sessions",
            font: { size: 10, family: "Raleway, sans-serif", weight: 700 as const },
            color: "#7b8794",
          },
        },
        y2: {
          position: "right" as const,
          beginAtZero: true,
          ticks: tick,
          grid: { display: false },
          border: { display: false },
          title: {
            display: true,
            text: "Acres",
            font: { size: 10, family: "Raleway, sans-serif", weight: 700 as const },
            color: "#7b8794",
          },
        },
      },
    };
  }, []);

  const topStops = pareto.slice(0, 4);
  const maxCount = topStops[0]?.count || 1;

  // The filter stays mounted through load/error states so the range the user
  // just picked doesn't vanish while the refetch is in flight.
  const filter = <DateRangeFilter value={range} onChange={onRangeChange} />;

  if (loading)
    return (
      <>
        {filter}
        <p style={{ color: "var(--color-text-secondary)", fontSize: 12 }}>Loading…</p>
      </>
    );
  if (error)
    return (
      <>
        {filter}
        <p style={{ color: "var(--color-text-danger)", fontSize: 12 }}>
          Couldn't load data: {error}
        </p>
      </>
    );

  return (
    <>
      {filter}
      <div className="kg">
        <Kpi icon="ti-clock" label="LABOR HOURS SAVED" value={totals.laborHours.toFixed(1)} unit=" hrs" sub="vs. manual crew" />
        <Kpi icon="ti-currency-dollar" iconColor="#22c55e" label="VALUE DELIVERED" value={`$${Math.round(totals.laborValue)}`} sub={`at $${CREW_RATE_PER_HOUR}/hr crew rate`} />
        <Kpi icon="ti-map" label="AREA COVERED" value={totals.totalAcres.toFixed(2)} unit=" ac" sub="across all sites" />
        <Kpi icon="ti-circle-check" label="SESSION COMPLETION" value={String(totals.completionPct)} unit="%" sub={`${totals.completed} of ${totals.totalSessions} sessions`} />
      </div>
      <div className="kg" style={{ marginBottom: 10 }}>
        <Kpi label="MOWERS ACTIVE" value={String(totals.mowersActive)} sub="this period" />
        <Kpi label="HUMAN INTERVENTIONS" value={String(totals.totalInterventions)} sub="sessions needing assist" />
        <Kpi
          label="ACRES / INTERVENTION"
          value={totals.acresPerIntervention != null ? totals.acresPerIntervention.toFixed(2) : "—"}
          sub="higher = more autonomous"
        />
        <Kpi label="AVG COVERAGE RATE" value={totals.avgProd.toFixed(2)} unit=" ac/hr" sub="fleet average" />
      </div>
      <div className="cr">
        <div className="cd" style={{ marginBottom: 0 }}>
          <p className="ct">DAILY COVERAGE &amp; SESSIONS</p>
          <div style={{ height: 220 }}>
            {daily.length === 0 ? (
              <p style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
                No activity in this period.
              </p>
            ) : (
              <Bar data={chartData} options={chartOptions} />
            )}
          </div>
        </div>
        <div className="cd" style={{ marginBottom: 0 }}>
          <p className="ct">WHY MOWERS STOPPED</p>
          {topStops.length === 0 && (
            <p style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>No stops in this period.</p>
          )}
          {topStops.map((s) => (
            <div className="ii" key={s.category}>
              <span className="ic">{s.count}</span>
              <div
                className="ib"
                style={{ width: `${Math.max(8, (s.count / maxCount) * 120)}px` }}
              />
              <span className="il">{s.category}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ height: 8 }} />
      <div className="cd" style={{ marginBottom: 0 }}>
        <p className="ct">SESSION OUTCOMES — {outcomes.total} TOTAL</p>
        <div className="outcome-row">
          <OutcomeBar label="Completed autonomously" count={outcomes.completed} pct={outcomes.pct(outcomes.completed)} color="#22c55e" />
          <OutcomeBar label="Paused, resumed without assist" count={outcomes.pausedResumed} pct={outcomes.pct(outcomes.pausedResumed)} color="var(--ob)" />
          <OutcomeBar label="Required human intervention" count={outcomes.intervened} pct={outcomes.pct(outcomes.intervened)} color="#f59e0b" />
        </div>
        <p style={{ fontSize: 10, color: "var(--color-text-tertiary)", margin: "10px 0 0" }}>
          {100 - outcomes.pct(outcomes.intervened)}% of sessions required no human presence on site.
        </p>
      </div>
    </>
  );
}

function Kpi({
  icon,
  iconColor,
  label,
  value,
  unit,
  sub,
}: {
  icon?: string;
  iconColor?: string;
  label: string;
  value: string;
  unit?: string;
  sub?: string;
}) {
  return (
    <div className="kc">
      {icon && (
        <div className="ki" style={iconColor ? { color: iconColor } : undefined}>
          <i className={"ti " + icon} aria-hidden="true" />
        </div>
      )}
      <p className="kl">{label}</p>
      <p className="kv">
        {value}
        {unit && <span style={{ fontSize: 14 }}>{unit}</span>}
      </p>
      {sub && <p className="ks">{sub}</p>}
    </div>
  );
}

function OutcomeBar({ label, count, pct, color }: { label: string; count: number; pct: number; color: string }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{label}</span>
        <span style={{ fontFamily: "'Roboto Slab', Georgia, serif", fontWeight: 700, fontSize: 13 }}>
          {count}{" "}
          <span style={{ fontSize: 10, fontFamily: "'Raleway', sans-serif", fontWeight: 400, color: "var(--color-text-tertiary)" }}>
            {pct}%
          </span>
        </span>
      </div>
      <div className="outcome-bar-track">
        <div className="outcome-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
